// Campos del step api_call. Lo usan el panel lateral (compact) y el editor
// grande del step, para que los dos editen exactamente lo mismo.
//
// Quien lo monta le pone `key={nodeId}`: Timeout, Body y los editores
// clave/valor guardan el texto en estado local (para no interrumpir la
// escritura) y deben reiniciarse al cambiar de step.
import React, { useState } from "react";
import StyledSelect from "../ui/StyledSelect";
import { DictKeyValueEditor } from "../ui/KeyValueEditor";
import ApiCredentialPicker from "./ApiCredentialPicker";
import {
  DEFAULT_API_CALL_TIMEOUT,
  formatApiCallBody,
  parseApiCallBody,
  parseApiCallTimeout,
} from "./apiCallStep";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const styles = {
  compact: {
    wrapper: "space-y-4",
    label: "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2",
    input: "w-full px-3 py-2 text-xs bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500/50",
    hint: "text-[10px] text-gray-500 mt-1",
    grid: "grid grid-cols-2 gap-3",
    bodyRows: 3,
    bodyLabel: "Body",
  },
  full: {
    wrapper: "space-y-6",
    label: "block text-sm font-semibold text-white mb-2",
    input: "w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20",
    hint: "text-xs text-gray-500 mt-1",
    grid: "grid grid-cols-2 gap-4",
    bodyRows: 5,
    bodyLabel: "Request Body",
  },
};

const ApiCallFields = ({ config, onFieldChange, compact = false }) => {
  const s = compact ? styles.compact : styles.full;
  // Se guarda solo un timeout válido; el texto puede quedar vacío mientras se escribe.
  const [timeoutText, setTimeoutText] = useState(String(config.timeout || DEFAULT_API_CALL_TIMEOUT));
  // El body se muestra tal cual se escribe; reformatearlo movería el cursor.
  const [bodyText, setBodyText] = useState(() => formatApiCallBody(config.body));

  const handleTimeoutChange = (text) => {
    setTimeoutText(text);
    const value = parseApiCallTimeout(text);
    if (value !== null) onFieldChange("timeout", value);
  };

  const handleTimeoutBlur = () => {
    if (parseApiCallTimeout(timeoutText) === null) {
      setTimeoutText(String(config.timeout || DEFAULT_API_CALL_TIMEOUT));
    }
  };

  const handleBodyChange = (text) => {
    setBodyText(text);
    onFieldChange("body", parseApiCallBody(text));
  };

  return (
    <div className={s.wrapper}>
      <ApiCredentialPicker
        compact={compact}
        credentialId={config.credential_id}
        url={config.url}
        queryParams={config.query_params}
        onCredentialChange={(id) => onFieldChange("credential_id", id)}
      />
      <div>
        <label className={s.label}>
          {config.credential_id ? "URL (path)" : "URL"} <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          className={`${s.input} font-mono`}
          placeholder={config.credential_id ? "path, e.g. reports/sales-sensors/events" : "https://api.example.com/endpoint"}
          value={config.url || ""}
          onChange={(e) => onFieldChange("url", e.target.value)}
        />
      </div>
      <div>
        <label className={s.label}>Query params</label>
        <DictKeyValueEditor
          compact={compact}
          value={config.query_params}
          onChange={(v) => onFieldChange("query_params", v)}
          keyPlaceholder="param"
          valuePlaceholder="value or {{context:...}}"
          addLabel="Add query param"
        />
      </div>
      <div>
        <label className={s.label}>Headers</label>
        <DictKeyValueEditor
          compact={compact}
          value={config.headers}
          onChange={(v) => onFieldChange("headers", v)}
          keyPlaceholder="Header"
          valuePlaceholder="value"
          addLabel="Add header"
        />
        <p className={s.hint}>Merged over the credential's default headers.</p>
      </div>
      <div className={s.grid}>
        <div>
          <label className={s.label}>Method</label>
          {compact ? (
            <select
              className={s.input}
              value={config.method || "GET"}
              onChange={(e) => onFieldChange("method", e.target.value)}
            >
              {METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          ) : (
            <StyledSelect
              value={config.method || "GET"}
              onChange={(v) => onFieldChange("method", v)}
              options={METHODS}
            />
          )}
        </div>
        <div>
          <label className={s.label}>Timeout (s)</label>
          <input
            type="number"
            min="1"
            aria-label="Timeout (s)"
            className={s.input}
            value={timeoutText}
            onChange={(e) => handleTimeoutChange(e.target.value)}
            onBlur={handleTimeoutBlur}
          />
        </div>
      </div>
      <div>
        <label className={s.label}>{s.bodyLabel}</label>
        <textarea
          aria-label={s.bodyLabel}
          className={`${s.input} font-mono`}
          rows={s.bodyRows}
          placeholder="{}"
          value={bodyText}
          onChange={(e) => handleBodyChange(e.target.value)}
        />
      </div>
    </div>
  );
};

export default ApiCallFields;
